import { addChatMessage, addOrOpenChat } from '@db/Chats';
import { getUsersIn } from '@db/Users';
import { AppQueryDomain, AppQueryKeySegment, createQueryKey, useAppMutation } from '@helpers/query';
import type { Chat, UserInfo } from '@types';

export type UseAddOrOpenChatMutationOptions = {
  /** Current user's subscribed chats used to detect an existing direct chat. */
  chats: readonly Chat[];
};

export type AddOrOpenChatMutationVariables = {
  /** Users selected for the chat, excluding the current user if not already present. */
  users: readonly UserInfo[];
};

export type AddOrOpenChatWithUserIdsMutationVariables = {
  /** User ids to include in the chat, excluding the current user if not already present. */
  userIds: readonly string[];
};

export type AddChatMessageMutationVariables = {
  /** Chat document id that owns the new message. */
  chatId: string;
  /** Message text to append to the chat. */
  text: string;
};

export type AddOrOpenChatWithMessageMutationVariables = {
  /** Users selected for the chat, excluding the current user if not already present. */
  users: readonly UserInfo[];
  /** First message text to send after the chat is created or opened. */
  text: string;
};

export function useAddOrOpenChatMutation({ chats }: UseAddOrOpenChatMutationOptions) {
  return useAppMutation<string, Error, AddOrOpenChatMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.CHATS, AppQueryKeySegment.CREATE_MUTATION),
    mutationFn: ({ users }) => addOrOpenChat(chats, users),
  });
}

export function useAddOrOpenChatWithUserIdsMutation({ chats }: UseAddOrOpenChatMutationOptions) {
  return useAppMutation<string, Error, AddOrOpenChatWithUserIdsMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.CHATS, AppQueryKeySegment.CREATE_MUTATION),
    mutationFn: async ({ userIds }) => {
      const users = await getUsersIn([...userIds]);
      return addOrOpenChat(chats, users);
    },
  });
}

export function useAddChatMessageMutation() {
  return useAppMutation<
    Awaited<ReturnType<typeof addChatMessage>>,
    Error,
    AddChatMessageMutationVariables
  >({
    mutationKey: createQueryKey(
      AppQueryDomain.CHATS,
      AppQueryKeySegment.CREATE_MUTATION,
      'message'
    ),
    mutationFn: ({ chatId, text }) => addChatMessage(chatId, text),
  });
}

export function useAddOrOpenChatWithMessageMutation({ chats }: UseAddOrOpenChatMutationOptions) {
  return useAppMutation<string, Error, AddOrOpenChatWithMessageMutationVariables>({
    mutationKey: createQueryKey(
      AppQueryDomain.CHATS,
      AppQueryKeySegment.CREATE_MUTATION,
      'message'
    ),
    mutationFn: async ({ users, text }) => {
      const chatId = await addOrOpenChat(chats, users);
      await addChatMessage(chatId, text);
      return chatId;
    },
  });
}
